/**
 * Materieel > Garantie - los garantieregister op serienummer.
 *
 * Bewust niet gekoppeld aan een artikel, uniek item of uitgifte: bij een defect
 * (bv. een kapotte accu) typt de gebruiker het serie- of registratienummer in en
 * ziet direct of en tot wanneer er garantie op zit. Gedeelde schema's: de API
 * parst ermee en het beheerform valideert er client-side mee vóór verzenden.
 */

import { z } from 'zod'

const YMD_RE = /^\d{4}-\d{2}-\d{2}$/

/** Date-only `yyyy-mm-dd` die ook als kalenderdatum bestaat (geen 31-02). */
const ymd = z
  .string()
  .regex(YMD_RE, 'Gebruik een geldige datum')
  .refine((v) => {
    const d = new Date(`${v}T00:00:00.000Z`)
    return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v
  }, 'Gebruik een geldige datum')

/** Leeg veld = `null`, zodat het form een leeg tekstveld 1:1 kan sturen. */
const optioneleTekst = (max: number) =>
  z
    .string()
    .trim()
    .max(max, `Maximaal ${max} tekens`)
    .nullable()
    .transform((v) => (v === '' ? null : v))

// ─── Status ──────────────────────────────────────────────────────────────────

export const GARANTIE_STATUSSEN = ['GELDIG', 'BINNENKORT', 'VERLOPEN'] as const
export type GarantieStatus = (typeof GARANTIE_STATUSSEN)[number]

export const GARANTIE_STATUS_LABELS: Record<GarantieStatus, string> = {
  GELDIG: 'Onder garantie',
  BINNENKORT: 'Verloopt binnenkort',
  VERLOPEN: 'Verlopen',
}

/** Vanaf zoveel dagen vóór de vervaldatum telt een garantie als "binnenkort". */
export const GARANTIE_BINNENKORT_DAGEN = 90

/**
 * Status van een garantie op `vandaagYmd` (NL-kalenderdag). De vervaldag zelf
 * valt nog onder garantie; de dag erna is hij verlopen.
 */
export function garantieStatus(vervaldatumYmd: string, vandaagYmd: string): GarantieStatus {
  if (vervaldatumYmd < vandaagYmd) return 'VERLOPEN'
  const dagen = garantieDagenResterend(vervaldatumYmd, vandaagYmd)
  return dagen <= GARANTIE_BINNENKORT_DAGEN ? 'BINNENKORT' : 'GELDIG'
}

/** Kalenderdagen van `vandaagYmd` tot en met de vervaldatum (negatief = verlopen). */
export function garantieDagenResterend(vervaldatumYmd: string, vandaagYmd: string): number {
  const eind = Date.parse(`${vervaldatumYmd}T00:00:00.000Z`)
  const nu = Date.parse(`${vandaagYmd}T00:00:00.000Z`)
  return Math.round((eind - nu) / 86_400_000)
}

/**
 * Zoeksleutel van een serie-/registratienummer: zonder spaties en de gangbare
 * scheidingstekens (`|`, `-`, `/`, `.`), kleine letters. Leveranciers noteren
 * "0710171 | 260120"; op het typeplaatje of in een mail staat "0710171260120".
 */
export function garantieZoeksleutel(nummer: string): string {
  return nummer.replace(/[\s|\-/.]+/g, '').toLowerCase()
}

// ─── Beheer-schema (create + update body) ───────────────────────────────────

export const garantieRegistratieSchema = z
  .object({
    merk: z.string().trim().min(1, 'Vul het merk in').max(100, 'Maximaal 100 tekens'),
    type: z.string().trim().min(1, 'Vul het type in').max(200, 'Maximaal 200 tekens'),
    serienummer: z
      .string()
      .trim()
      .min(1, 'Vul het serienummer in')
      .max(100, 'Maximaal 100 tekens'),
    registratienummer: optioneleTekst(100),
    aankoopdatum: ymd.nullable(),
    vervaldatum: ymd,
    opmerking: optioneleTekst(1000),
  })
  .superRefine((v, ctx) => {
    if (v.aankoopdatum && v.vervaldatum < v.aankoopdatum) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['vervaldatum'],
        message: 'De vervaldatum ligt vóór de aankoopdatum',
      })
    }
  })
export type GarantieRegistratieInput = z.infer<typeof garantieRegistratieSchema>

// ─── Lijst-query ─────────────────────────────────────────────────────────────

export const garantieListQuerySchema = z.object({
  /** Zoekt in serienummer, registratienummer, merk en type. */
  q: z.string().trim().max(100).optional(),
  status: z.enum(GARANTIE_STATUSSEN).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(50),
})
export type GarantieListQuery = z.infer<typeof garantieListQuerySchema>

// ─── Read-DTO ────────────────────────────────────────────────────────────────

export interface GarantieRegistratieDTO {
  id: string
  merk: string
  type: string
  serienummer: string
  registratienummer: string | null
  /** yyyy-mm-dd of null. */
  aankoopdatum: string | null
  /** yyyy-mm-dd. */
  vervaldatum: string
  opmerking: string | null
  /** Afgeleid op de NL-kalenderdag van het verzoek. */
  status: GarantieStatus
  dagenResterend: number
  createdAt: string
  updatedAt: string
}
